import React from 'react';
import AtlasComponent from '../../components/organisms/AtlasComponent/AtlasComponent';
import UserListOrganism from '../../components/organisms/UserList/UserListOrganism';
import ProfileOrganism from '../../components/organisms/ProfileOrganism/ProfileOrganism';
import PreferencesOrganism from '../../components/organisms/PreferencesOrganism/PreferencesOrganism';
import SecurityOrganism from '../../components/organisms/SecurityOrganism/SecurityOrganism';
import TreeMapOrganism from '../../components/organisms/TreeMapOrganism/TreeMapOrganism';
import ArmillaryViewOrganism from '../../components/organisms/ArmillaryView/ArmillaryViewOrganism';
import ArmillaryActionsMolecule from '../../components/molecules/ArmillaryActions/ArmillaryActionsMolecule';
import TaskListOrganism from '../../components/organisms/TaskList/TaskListOrganism';
import ScorecardOrganism from '../../components/organisms/ScorecardOrganism/ScorecardOrganism';
import type { Adapter, CompositionEnvironment } from '../types';
import { FALLBACK_METASPACE_SYSTEM_NAME } from '../../constants/metaspaces';

/**
 * Adapters for the legacy metaspace organisms, ported from `MetaspaceGrid`'s `OrganismRenderer`
 * switch. Every value that switch read from Redux/router (`organizationId`, `identityId`, `userId`,
 * `sphereId`, `navigate`) is read from `ctx.env` here instead — the host (Center-PWA's
 * `SpaceRendererOrganism` usage, Phase 4) injects it as props, so `CompositionHost` itself never
 * imports `@center-pwa/*` (spec § Verified seams #7).
 */

function resolveSphereId(sphereIdSource: unknown, env: CompositionEnvironment, fallback?: string): string | undefined {
  if (sphereIdSource === 'identity') return env.identityId;
  if (sphereIdSource === 'organization') return env.organizationId;
  if (sphereIdSource === 'user') return env.userId;
  if (sphereIdSource === 'current' || sphereIdSource === 'sphere') return env.sphereId;
  return fallback;
}

export const AtlasAdapter: Adapter = ({ componentId, props, ctx }) => {
  const sphereId = resolveSphereId(props.sphereIdSource, ctx.env, props.sphereId as string | undefined);
  return (
    <div data-component-id={componentId}>
      <AtlasComponent
        contextSystemName={(props.contextSystemName as string) ?? ''}
        viewSystemName={props.viewSystemName as string | undefined}
        sphereId={sphereId}
        organizationId={ctx.env.organizationId ?? ''}
        onChatBubbleClick={ctx.env.onChatBubbleClick}
        // NOT `Boolean(...)`: `AtlasComponent` defaults `showToolbar` to `true`, so coercing an
        // unset env value to `false` would invert that default for every host that builds a partial
        // `env` (the composer preview and the agent surface both will). Pass `undefined` through and
        // let the component own its default.
        showToolbar={ctx.env.showToolbar}
        onNewContext={() => ctx.env.onNavigate?.('/contexts/new/blank')}
        onManageContexts={() => ctx.env.onNavigate?.('/contexts')}
      />
    </div>
  );
};

export const UsersAdapter: Adapter = ({ componentId, ctx }) => (
  <div data-component-id={componentId}>{ctx.env.sphereId ? <UserListOrganism organizationId={ctx.env.sphereId} /> : null}</div>
);

export const ProfileAdapter: Adapter = ({ componentId, ctx }) => (
  <div data-component-id={componentId}>
    {ctx.env.identityId ? <ProfileOrganism identityId={ctx.env.identityId} organizationId={ctx.env.organizationId ?? ''} /> : null}
  </div>
);

export const PreferencesAdapter: Adapter = ({ componentId, ctx }) => (
  <div data-component-id={componentId}>{ctx.env.identityId ? <PreferencesOrganism identityId={ctx.env.identityId} /> : null}</div>
);

export const SecurityAdapter: Adapter = ({ componentId, ctx }) => (
  <div data-component-id={componentId}>{ctx.env.identityId ? <SecurityOrganism identityId={ctx.env.identityId} /> : null}</div>
);

export const TreeMapAdapter: Adapter = ({ componentId, props, ctx }) => {
  const config = (props.config as Record<string, unknown>) ?? {};
  const rootSphereId = (config.rootSphereId as string) ?? ctx.env.sphereId;
  return (
    <div data-component-id={componentId}>
      <TreeMapOrganism
        contextSystemName={config.contextSystemName as string | undefined}
        organizationId={ctx.env.organizationId ?? ''}
        rootSphereId={rootSphereId}
        viewSystemName={config.viewSystemName as string | undefined}
        editable={(config.editable as boolean) ?? true}
        initialCardView={(config.initialCardView as 'standard' | 'minimal' | 'expanded') ?? 'standard'}
        showBreadcrumbs={(config.showBreadcrumbs as boolean) ?? true}
        showFilters={(config.showFilters as boolean) ?? true}
        showAI={(config.showAI as boolean) ?? false}
        defaultPageSize={(config.defaultPageSize as number) ?? 10}
        useGridLayout={(config.useGridLayout as boolean) ?? true}
        onNewContext={() => ctx.env.onNavigate?.('/contexts/new/blank')}
        onManageContexts={() => ctx.env.onNavigate?.('/contexts')}
      />
    </div>
  );
};

export const ArmillaryViewAdapter: Adapter = ({ componentId, props, ctx }) => (
  <div data-component-id={componentId}>
    <ArmillaryViewOrganism
      sphereId={ctx.env.sphereId ?? ''}
      centerImagePath={props.centerImagePath as string | undefined}
      onNodeSelect={(sphere) => {
        if (sphere?.id) ctx.env.onNavigate?.(`/sphere/${sphere.id}/metaspace/${FALLBACK_METASPACE_SYSTEM_NAME}`);
      }}
      onConversationClick={() => ctx.env.onChatBubbleClick?.(ctx.env.sphereId ?? '')}
    />
  </div>
);

export const ArmillaryActionsAdapter: Adapter = ({ componentId, ctx }) => (
  <div data-component-id={componentId}>
    <ArmillaryActionsMolecule rootSphereId={ctx.env.sphereId ?? ''} activeSphereId={ctx.env.sphereId ?? ''} isHidden={Boolean(ctx.env.toolbarOpen)} />
  </div>
);

export const TaskListAdapter: Adapter = ({ componentId, props, ctx }) => {
  const settings = (props.taskListSettings as Record<string, unknown>) ?? {};
  const sphereId = resolveSphereId(props.sphereIdSource, ctx.env, (settings.sphereId as string) ?? ctx.env.userId);

  if (!sphereId) return <div data-component-id={componentId} />;

  return (
    <div data-component-id={componentId}>
      <TaskListOrganism
        sphereId={sphereId}
        title={(props.title as string) ?? undefined}
        collapsible={((props.settings as Record<string, unknown>)?.collapsible as boolean) ?? true}
        maxRows={settings.maxRows as number | undefined}
        onEditSphere={ctx.env.onEditSphere}
        sphereChange={ctx.env.sphereChange}
        fetchPermissionList={ctx.env.fetchPermissionList}
        settings={{
          showPriority: settings.showPriority as boolean | undefined,
          showAssignee: settings.showAssignee as boolean | undefined,
          showDueDate: settings.showDueDate as boolean | undefined,
          enableInlineActions: (settings.enableInlineActions as boolean) ?? true,
          refreshInterval: settings.refreshInterval as number | undefined,
          filterByStatus: settings.filterByStatus as never,
          filterByTaskType: settings.filterByTaskType as never,
          filterByCheckInStatus: settings.filterByCheckInStatus as never,
          includeVirtual: settings.includeVirtual as boolean | undefined,
          lookaheadDays: settings.lookaheadDays as number | undefined,
          sortBy: settings.sortBy as never,
          sortOrder: settings.sortOrder as never,
        }}
      />
    </div>
  );
};

export const ScorecardAdapter: Adapter = ({ componentId, props, ctx }) => {
  const scorecardSettings = (props.scorecardSettings as Record<string, unknown>) ?? {};
  const rootSphereId = resolveSphereId(props.sphereIdSource, ctx.env, (scorecardSettings.sphereId as string) ?? (props.sphereId as string));

  if (!rootSphereId || !scorecardSettings.viewSystemName) return <div data-component-id={componentId} />;

  return (
    <div data-component-id={componentId}>
      <ScorecardOrganism
        viewSystemName={scorecardSettings.viewSystemName as string}
        rootSphereId={rootSphereId}
        orbitSphere={ctx.env.scorecardOrbitSphere as never}
        organizationId={ctx.env.organizationId ?? ''}
      />
    </div>
  );
};
